import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import {
  ACTION_LABEL,
  addComment,
  gateAction,
  listApprovals,
  listComments,
  openChangeRequests,
  performAction,
  resolveComment,
  type ApprovalAction,
  type ApprovalRow,
  type CommentRow,
} from '../../data/collabRepo';
import { useUserDirectory } from '../../hooks/useUserDirectory';
import { listMembers } from '../../data/repo';
import { pushNotifications } from '../../data/notifyRepo';
import { toHash } from '../../boot/router';
import { VERSION_STATUS_META, type VersionStatus } from '../../data/types';
import type { AppUser } from '../../boot/session';
import type { Editor } from '../editor';

/**
 * 검토 패널 — 댓글 · 승인 (작업지시서 P4-11 / P4-12)
 *
 * ── 왜 댓글과 승인을 한 화면에 두는가 ───────────────────────────────────
 *
 *   검토자는 "댓글 다 처리됐나" 를 보고 승인을 누른다. 두 화면으로 나누면
 *   미해결 요청을 못 보고 승인하게 된다. 붙여 놓고 **승인 버튼 옆에**
 *   미해결 건수를 띄운다.
 *
 * ── 도면 위치를 가리키는 댓글 ───────────────────────────────────────────
 *
 *   "여기 통로가 좁다" 는 좌표 없이는 쓸모가 없다. 선택한 객체가 있으면
 *   그 객체에, 없으면 현재 커서 위치에 앵커를 건다.
 */

const REVIEWER_ROLES = new Set(['admin', 'cad_admin', 'reviewer']);

export function ReviewPanel({
  editor,
  projectId,
  drawingId,
  drawingName,
  versionId,
  status,
  user,
  onStatusChange,
}: {
  editor: Editor;
  projectId: string;
  drawingId: string;
  drawingName: string;
  versionId: string;
  status: VersionStatus;
  user: AppUser;
  onStatusChange: (next: VersionStatus) => void;
}) {
  const [comments, setComments] = useState<CommentRow[]>([]);
  const [approvals, setApprovals] = useState<ApprovalRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [draft, setDraft] = useState('');
  const [asChangeRequest, setAsChangeRequest] = useState(false);
  const [reasonFor, setReasonFor] = useState<ApprovalAction | null>(null);
  const [reason, setReason] = useState('');

  const { activeEntries, nameOf } = useUserDirectory();
  /** 작성자·승인자 이름 표시. 저장은 여전히 이메일이다 — 알림도 결국 메일로 간다. */
  const displayName = nameOf;
  /**
   * 댓글 본문에 남아 있을 수 있는 옛 "@메일주소" 형식만 "@이름" 으로 바꿔 보여준다.
   * 새로 쓰는 댓글은 애초에 "@이름" 을 그대로 입력하므로 손댈 게 없다.
   */
  const renderBody = useCallback(
    (body: string) =>
      body.replace(/@([\w.+-]+@[\w-]+\.[\w.-]+)/g, (whole, email: string) => {
        const name = nameOf(email);
        return name === email ? whole : `@${name}`;
      }),
    [nameOf]
  );

  // "@이름" 자동완성 — 커서 바로 앞의 "@단어" 를 사람 이름 후보와 맞춰 본다
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mentionMatches = useMemo(() => {
    if (mentionQuery === null) return [];
    const q = mentionQuery.toLowerCase();
    return activeEntries.filter((e) => e.name.toLowerCase().includes(q)).slice(0, 6);
  }, [activeEntries, mentionQuery]);

  function syncMentionQuery(value: string, caret: number) {
    const upToCaret = value.slice(0, caret);
    const m = /@([^\s@]*)$/.exec(upToCaret);
    setMentionQuery(m ? m[1]! : null);
  }

  function handleDraftChange(e: ChangeEvent<HTMLTextAreaElement>) {
    const value = e.target.value;
    setDraft(value);
    syncMentionQuery(value, e.target.selectionStart ?? value.length);
  }

  function pickMention(name: string) {
    const el = textareaRef.current;
    const caret = el?.selectionStart ?? draft.length;
    const upToCaret = draft.slice(0, caret);
    const m = /@([^\s@]*)$/.exec(upToCaret);
    if (!m) return;
    const start = caret - m[0].length;
    const next = `${draft.slice(0, start)}@${name} ${draft.slice(caret)}`;
    setDraft(next);
    setMentionQuery(null);
    requestAnimationFrame(() => {
      const pos = start + name.length + 2;
      el?.focus();
      el?.setSelectionRange(pos, pos);
    });
  }

  const isReviewer = REVIEWER_ROLES.has(user.role);

  const reload = useCallback(async () => {
    try {
      const [c, a] = await Promise.all([listComments(versionId), listApprovals(versionId)]);
      setComments(c);
      setApprovals(a);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [versionId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function run(what: string, fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await reload();
    } catch (e) {
      setError(`${what} 실패: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  const versionLink = toHash({ name: 'version', drawingId, versionId });
  const actorLabel = displayName(user.email);

  /** 멘션된 사람에게만 — 프로젝트 참가자가 아니어도 지목됐으면 알려야 한다 */
  async function notifyMentions(mentions: string[], commentBody: string) {
    const targets = mentions.filter((e) => e !== user.email);
    if (targets.length === 0) return;
    await pushNotifications(targets, (email) => ({
      toEmail: email,
      title: `[${drawingName}] 댓글에서 멘션되었습니다`,
      body: `${actorLabel}: ${commentBody.slice(0, 80)}`,
      hashPath: versionLink,
    }));
  }

  /** 검토수정완료·승인완료 — 도면이 속한 프로젝트 참가자 전체에게 */
  async function notifyProjectParticipants(title: string, body: string) {
    try {
      const members = await listMembers(projectId);
      const targets = members.map((m) => m.email).filter((e) => e !== user.email);
      if (targets.length === 0) return;
      await pushNotifications(targets, (email) => ({ toEmail: email, title, body, hashPath: versionLink }));
    } catch {
      /* 알림은 부가 기능 — 실패해도 원래 동작(승인·댓글 처리)을 막지 않는다 */
    }
  }

  const open = openChangeRequests(comments);

  /** 선택된 객체 → 없으면 커서 위치 → 없으면 앵커 없음 */
  function currentAnchor() {
    const selected = [...editor.selection][0];
    if (selected) return { entityId: selected, anchor: null };
    if (editor.cursor) {
      return {
        entityId: null,
        anchor: { type: 'point' as const, x: editor.cursor.x, y: editor.cursor.y },
      };
    }
    return { entityId: null, anchor: null };
  }

  const actions = availableActions(status);

  return (
    <div className="review">
      {error && (
        <p className="cad-panel__notice cad-panel__notice--error" role="alert">
          {error}
        </p>
      )}

      {/* ---- 승인 ---- */}
      <div className="review__status">
        <span style={{ color: VERSION_STATUS_META[status].color }}>
          <span aria-hidden="true">{VERSION_STATUS_META[status].icon}</span>{' '}
          {VERSION_STATUS_META[status].label}
        </span>
        {open.length > 0 && (
          // 승인 버튼 바로 옆에 둔다 — 못 보고 승인하는 일을 막는다
          <span className="review__blocked">미해결 수정 요청 {open.length}건</span>
        )}
      </div>

      <div className="review__actions">
        {actions.map((action) => {
          const gate = gateAction(action, status, comments, isReviewer);
          const needsReason = action === 'reject' || action === 'changes_requested';
          return (
            <button
              key={action}
              type="button"
              className={`btn btn--sm ${action === 'approve' || action === 'publish' ? 'btn--primary' : 'btn--ghost'}`}
              disabled={busy || !gate.ok}
              title={gate.reason ?? ACTION_LABEL[action]}
              onClick={() => {
                if (needsReason) {
                  setReasonFor(action);
                  return;
                }
                void run(ACTION_LABEL[action], async () => {
                  const next = await performAction(versionId, action, status, null);
                  onStatusChange(next);
                  if (action === 'approve') {
                    void notifyProjectParticipants(
                      `[${drawingName}] 승인되었습니다`,
                      `${actorLabel}님이 이 버전을 승인했습니다.`
                    );
                  }
                });
              }}
            >
              {ACTION_LABEL[action]}
            </button>
          );
        })}
        {actions.length === 0 && (
          <span className="cad-panel__hint">이 상태에서 할 수 있는 작업이 없습니다.</span>
        )}
      </div>

      {reasonFor && (
        <form
          className="layer-form"
          onSubmit={(e) => {
            e.preventDefault();
            void run(ACTION_LABEL[reasonFor], async () => {
              const next = await performAction(versionId, reasonFor, status, reason);
              onStatusChange(next);
              setReasonFor(null);
              setReason('');
            });
          }}
        >
          <p className="cad-panel__hint">
            {ACTION_LABEL[reasonFor]} 사유를 적어 주세요. 받는 사람이 무엇을 고쳐야 할지 알 수
            있어야 합니다.
          </p>
          <textarea
            className="input"
            rows={3}
            value={reason}
            autoFocus
            onChange={(e) => setReason(e.target.value)}
          />
          <div className="layer-form__actions">
            <button type="submit" className="btn btn--primary btn--sm" disabled={busy || !reason.trim()}>
              보내기
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => {
                setReasonFor(null);
                setReason('');
              }}
            >
              취소
            </button>
          </div>
        </form>
      )}

      {/* ---- 댓글 작성 ---- */}
      <form
        className="review__form"
        onSubmit={(e) => {
          e.preventDefault();
          const { entityId, anchor } = currentAnchor();
          void run('댓글 작성', async () => {
            const created = await addComment({
              versionId,
              body: draft,
              entityId,
              anchor,
              isChangeRequest: asChangeRequest,
              mentionDirectory: activeEntries,
            });
            setDraft('');
            setAsChangeRequest(false);
            setMentionQuery(null);
            void notifyMentions(created.mentions, created.body);
          });
        }}
      >
        <div className="review__mention-box">
          <textarea
            ref={textareaRef}
            className="input"
            rows={3}
            placeholder="댓글을 입력하세요. @이름 을 입력하면 멘션할 수 있습니다."
            value={draft}
            onChange={handleDraftChange}
            onKeyUp={(e) => syncMentionQuery(draft, e.currentTarget.selectionStart ?? draft.length)}
            onClick={(e) => syncMentionQuery(draft, e.currentTarget.selectionStart ?? draft.length)}
            onBlur={() => window.setTimeout(() => setMentionQuery(null), 150)}
          />
          {mentionQuery !== null && mentionMatches.length > 0 && (
            <ul className="mention-suggest">
              {mentionMatches.map((m) => (
                <li key={m.email}>
                  <button type="button" onMouseDown={(e) => { e.preventDefault(); pickMention(m.name); }}>
                    {m.name} <span className="mention-suggest__email">{m.email}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <label className="review__check">
          <input
            type="checkbox"
            checked={asChangeRequest}
            onChange={(e) => setAsChangeRequest(e.target.checked)}
          />
          수정 요청으로 등록 (해결 전에는 승인할 수 없습니다)
        </label>
        <p className="cad-panel__hint">
          {[...editor.selection][0]
            ? `선택한 객체에 답니다 (${[...editor.selection].length}개 중 1개)`
            : editor.cursor
              ? '현재 커서 위치에 답니다'
              : '도면 위치 없이 답니다'}
        </p>
        <button type="submit" className="btn btn--primary btn--sm" disabled={busy || !draft.trim()}>
          댓글 달기
        </button>
      </form>

      {/* ---- 댓글 목록 ---- */}
      <ul className="review__list">
        {comments.map((c) => (
          <li
            key={c.id}
            className={
              c.is_change_request && c.status === 'open'
                ? 'review-item review-item--cr'
                : 'review-item'
            }
          >
            <div className="review-item__head">
              <strong title={c.created_by}>{displayName(c.created_by)}</strong>
              <span className="review-item__when">
                {new Date(c.created_at).toLocaleString('ko-KR')}
              </span>
              {c.is_change_request && (
                <span className="review-item__tag">
                  {c.status === 'open' ? '수정 요청' : '해결됨'}
                </span>
              )}
            </div>
            <p className="review-item__body">{renderBody(c.body)}</p>

            <div className="review-item__foot">
              {(c.entity_id || c.anchor) && (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => {
                    if (c.entity_id) {
                      editor.selectOnly(c.entity_id);
                      editor.zoomSelection();
                    } else if (c.anchor?.type === 'point') {
                      editor.centerOn({ x: c.anchor.x, y: c.anchor.y });
                    }
                  }}
                >
                  위치 보기
                </button>
              )}
              {c.is_change_request && (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  disabled={busy}
                  onClick={() => {
                    const resolving = c.status === 'open';
                    void run('댓글 상태 변경', async () => {
                      await resolveComment(c.id, resolving);
                      if (resolving) {
                        void notifyProjectParticipants(
                          `[${drawingName}] 수정 요청이 해결되었습니다`,
                          `${actorLabel}님이 "${c.body.slice(0, 60)}" 요청을 해결로 표시했습니다.`
                        );
                      }
                    });
                  }}
                >
                  {c.status === 'open' ? '해결로 표시' : '다시 열기'}
                </button>
              )}
            </div>
          </li>
        ))}
        {comments.length === 0 && <p className="cad-panel__hint">댓글이 없습니다.</p>}
      </ul>

      {/* ---- 승인 이력 ---- */}
      {approvals.length > 0 && (
        <details className="review__history">
          <summary>승인 이력 {approvals.length}건</summary>
          <ul>
            {approvals.map((a) => (
              <li key={a.id}>
                <strong>{ACTION_LABEL[a.action]}</strong> · <span title={a.actor}>{displayName(a.actor)}</span> ·{' '}
                {new Date(a.created_at).toLocaleString('ko-KR')}
                {a.reason && <div className="review__reason">{a.reason}</div>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

/** 현재 상태에서 화면에 띄울 동작들 */
function availableActions(status: VersionStatus): ApprovalAction[] {
  switch (status) {
    case 'draft':
      return ['request', 'archive'];
    case 'in_review':
      return ['approve', 'changes_requested'];
    case 'changes_requested':
      return ['request', 'archive'];
    case 'approved':
      return ['publish', 'changes_requested', 'archive'];
    case 'published':
      return ['archive'];
    case 'archived':
      return [];
  }
}
